import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, 
  CheckCircle2, 
  XCircle,
  Phone,
  ArrowLeft,
  GraduationCap,
  Briefcase,
  MessageCircle,
  Shield,
  FileCheck,
  Users
} from "lucide-react";

const selectionCriteria = [
  {
    icon: GraduationCap,
    title: "הסמכה מוכרת",
    description: "וודאו שהממונה עבר הכשרה מוכרת ומחזיק בתעודת הסמכה תקפה ממשרד העבודה",
    checkpoints: [
      "תעודת ממונה בטיחות בתוקף",
      "השתלמויות עדכניות",
      "התמחות בענף הרלוונטי"
    ]
  },
  {
    icon: Briefcase,
    title: "ניסיון מעשי",
    description: "העדיפו ממונה עם ניסיון בענף שלכם או בענפים דומים מבחינת הסיכונים",
    checkpoints: [
      "שנות ניסיון בתחום",
      "היכרות עם הענף שלכם",
      "ניהול אירועים בעבר"
    ]
  },
  {
    icon: MessageCircle,
    title: "יכולות תקשורת",
    description: "ממונה טוב יודע לתקשר עם כל הדרגים בארגון - מעובדי שטח ועד הנהלה בכירה",
    checkpoints: [
      "יכולת להעביר הדרכות",
      "כתיבה ברורה ומקצועית",
      "יחסי אנוש טובים"
    ]
  },
  {
    icon: Shield,
    title: "גישה פרואקטיבית",
    description: "חפשו ממונה שמוביל שיפור מתמיד ולא רק מגיב לבעיות",
    checkpoints: [
      "יוזמות לשיפור בטיחות",
      "זיהוי סיכונים מראש",
      "מעקב ובקרה שוטפים"
    ]
  }
];

const redFlags = [
  "אין תעודת הסמכה או שהיא לא בתוקף",
  "חוסר ניסיון בענף או בענפים דומים",
  "חוסר זמינות או תגובתיות נמוכה",
  "גישה של 'לסמן וי' ללא עומק",
  "חוסר עדכון בשינויי חקיקה",
  "אין המלצות או רפרנסים"
];

const greenFlags = [
  "תעודות הסמכה מעודכנות",
  "ניסיון מוכח וממליצים",
  "זמינות גבוהה ותגובתיות",
  "גישה מקצועית ופרואקטיבית",
  "ידע עדכני בחקיקה",
  "יכולת תקשורת מעולה"
];

const questionsToAsk = [
  {
    question: "מה הניסיון שלך בענף שלנו?",
    why: "ממונה עם ניסיון בענף שלכם יזהה סיכונים מהר יותר ויביא פתרונות מוכחים"
  },
  {
    question: "איך אתה מתעדכן בשינויי חקיקה?",
    why: "תחום הבטיחות משתנה תדיר - חשוב שהממונה יהיה מעודכן"
  },
  {
    question: "מה הגישה שלך לבניית תרבות בטיחות?",
    why: "ממונה טוב לא רק אוכף - הוא מוביל שינוי תרבותי"
  },
  {
    question: "איך תטפל במקרה של תאונה?",
    why: "תשובה מקצועית תעיד על ניסיון והיערכות"
  },
  {
    question: "מה כלול בשירות שאתה מציע?",
    why: "הבנה ברורה של היקף השירות תמנע אי הבנות"
  },
  {
    question: "האם יש לך ביטוח אחריות מקצועית?",
    why: "ביטוח מקצועי מעיד על רצינות ומגן על הארגון"
  }
];

const faqs = [
  {
    question: "האם עדיף ממונה פנימי או חיצוני?",
    answer: "התשובה תלויה בגודל הארגון, תקציב ומורכבות. לארגונים קטנים ובינוניים, ממונה חיצוני לרוב יהיה חסכוני ומקצועי יותר. לארגונים גדולים עם סיכונים מורכבים, ממונה פנימי עשוי להיות עדיף."
  },
  {
    question: "מה ההבדל בין ממונה פנימי לחיצוני?",
    answer: "ממונה פנימי הוא עובד שכיר המשמש כממונה בטיחות בארגון. ממונה חיצוני הוא יועץ או חברה שמספקים את השירות ללא יחסי עובד-מעביד. שניהם צריכים להחזיק בתעודת הסמכה תקפה."
  },
  {
    question: "מה ההבדל בין ממונה בטיחות ליועץ בטיחות?",
    answer: "ממונה בטיחות ממלא תפקיד רשמי בארגון ונושא באחריות משפטית. יועץ בטיחות מספק ייעוץ נקודתי ללא אחריות שוטפת. ממונה יכול להיות גם יועץ, אך לא כל יועץ יכול להיות ממונה."
  }
];

export default function ChooseSafetyOfficer() {
  return (
    <Layout>
      <Helmet>
        <title>כיצד לבחור ממונה בטיחות מתאים | Adam Safety</title>
        <meta name="description" content="מדריך מקיף לבחירת ממונה בטיחות לארגון - קריטריונים, שאלות חשובות וטעויות שכדאי להימנע מהן." />
        <link rel="canonical" href="https://adam-safety.com/services/choose-safety-officer" />
        <meta property="og:title" content="כיצד לבחור ממונה בטיחות מתאים | Adam Safety" />
        <meta property="og:description" content="מדריך מקיף לבחירת ממונה בטיחות לארגון - קריטריונים, שאלות חשובות וטעויות שכדאי להימנע מהן." />
        <meta property="og:url" content="https://adam-safety.com/services/choose-safety-officer" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="כיצד לבחור ממונה בטיחות מתאים | Adam Safety" />
        <meta name="twitter:description" content="מדריך מקיף לבחירת ממונה בטיחות לארגון - קריטריונים, שאלות חשובות וטעויות שכדאי להימנע מהן." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <Search className="w-5 h-5" />
              <span className="text-sm font-medium">מדריך בחירה</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              כיצד לבחור ממונה בטיחות מתאים
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              מדריך מקיף עם כל מה שצריך לדעת לפני שבוחרים ממונה בטיחות לארגון - 
              קריטריונים, שאלות חשובות וטעויות נפוצות
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  קבל ייעוץ חינם
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Selection Criteria */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              קריטריונים לבחירת ממונה בטיחות
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              ארבעת הקריטריונים המרכזיים שיעזרו לכם לבחור את הממונה הנכון
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {selectionCriteria.map((criteria, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="icon-container mb-4">
                  <criteria.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{criteria.title}</h3>
                <p className="text-muted-foreground mb-4">{criteria.description}</p>
                <ul className="space-y-2">
                  {criteria.checkpoints.map((point, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-accent" />
                      <span className="text-muted-foreground">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Red Flags vs Green Flags */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              סימנים טובים וסימני אזהרה
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Green Flags */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-green-50 dark:bg-green-900/20 rounded-3xl p-8 border border-green-200 dark:border-green-800"
            >
              <h3 className="text-2xl font-bold text-green-700 dark:text-green-400 mb-6 flex items-center gap-2">
                <CheckCircle2 className="w-7 h-7" />
                סימנים חיוביים
              </h3>
              <ul className="space-y-4">
                {greenFlags.map((flag, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span className="text-green-800 dark:text-green-200">{flag}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Red Flags */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-red-50 dark:bg-red-900/20 rounded-3xl p-8 border border-red-200 dark:border-red-800"
            >
              <h3 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-6 flex items-center gap-2">
                <XCircle className="w-7 h-7" />
                סימני אזהרה
              </h3>
              <ul className="space-y-4">
                {redFlags.map((flag, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <span className="text-red-800 dark:text-red-200">{flag}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Questions to Ask */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות שחשוב לשאול
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              שאלות מפתח לשאול בפגישה עם מועמד לתפקיד ממונה בטיחות
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4">
            {questionsToAsk.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent font-bold">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-1">"{item.question}"</h3>
                    <p className="text-muted-foreground text-sm">{item.why}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              צריכים עזרה בבחירת ממונה?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              צוות המומחים שלנו ישמח לייעץ לכם בחינם ולעזור למצוא את הפתרון המתאים
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  שיחת ייעוץ חינם
                </Button>
              </Link>
              <Link to="/services/external-consulting">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  שירותי ממונה חיצוני
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}